import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { usePerms, type Area } from '@/lib/perms';

function Screen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 text-foreground-900">{children}</div>;
}

function Loading() {
  const { t } = useTranslation();
  return (
    <Screen>
      <p className="flex items-center gap-2 text-[12.5px] text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-[16px]" /> {t('ui.loading')}
      </p>
    </Screen>
  );
}

function Failed({ error, retry }: { error: string; retry: () => void }) {
  const { t } = useTranslation();
  return (
    <Screen>
      <div className="w-full max-w-[380px] rounded-lg border border-background-200 bg-background-50 p-5 text-center">
        <i className="ri-error-warning-line text-[22px] text-rose-600" />
        <p className="mt-2 text-[13px] font-semibold text-foreground-950">{t('ui.error')}</p>
        <p className="mt-1 text-[11.5px] text-foreground-500">{error}</p>
        <button
          type="button"
          onClick={retry}
          className="mt-4 inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md bg-primary-600 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-700"
        >
          <i className="ri-refresh-line text-[13px]" /> {t('ui.retry')}
        </button>
      </div>
    </Screen>
  );
}

/** Хаб: только сотрудники Treejar. Неавторизованных — на /portal/login, партнёров — в /portal. */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { me, loading, error, refetch } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (error) return <Failed error={error} retry={() => void refetch()} />;
  if (!me) return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  if (me.side !== 'staff') return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

/** Раздел хаба по матрице прав (Настройки → «Права ролей»): роль без чтения видит панель «Нет доступа», а не пустой экран. */
export function AreaGuard({ area, children }: { area: Area; children: ReactNode }) {
  const { t } = useTranslation();
  const { can } = usePerms();
  if (can(area)) return <>{children}</>;
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="w-full max-w-[420px] rounded-lg border border-background-200 bg-background-50 p-5 text-center">
        <i className="ri-lock-2-line text-[22px] text-foreground-400" />
        <p className="mt-2 text-[13px] font-semibold text-foreground-950">{t('perm.noAccessTitle')}</p>
        <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('perm.noAccessBody')}</p>
      </div>
    </div>
  );
}

/** Портал: только сотрудники партнёра. Сотрудников Treejar — в хаб. */
export function PortalGuard({ children }: { children: ReactNode }) {
  const { me, loading, error, refetch } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (error) return <Failed error={error} retry={() => void refetch()} />;
  if (!me) return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  if (me.side !== 'partner') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Разделы фазы 2: скрыты фичефлагом — редирект, а не экран на моках. */
export function FlagGuard({ flag, to, children }: { flag: keyof ReturnType<typeof useAuth>['flags']; to: string; children: ReactNode }) {
  const { flags } = useAuth();
  if (!flags[flag]) return <Navigate to={to} replace />;
  return <>{children}</>;
}
